import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Disc3, Download, Heart, ImagePlus, Loader2, Mic2, Pause, Play, Shuffle, Share2, StretchHorizontal, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { inputCls } from "./Chips";
import { AddToPlaylist } from "./AddToPlaylist";
import { KaraokeLyrics, KaraokeMode } from "./Karaoke";
import { usePlayer } from "../context/PlayerContext";
import { downloadUrl, errMsg, http } from "../lib/api";

const btnCls = "flex items-center gap-2 px-4 py-2.5 rounded-full border border-[var(--c-line)] text-sm text-zinc-200 hover:border-white hover:text-white transition-colors duration-200";
const copy = (txt, msg) => navigator.clipboard.writeText(txt).then(() => toast.success(msg));

const Btn = ({ testId, onClick, children, href }) => (href
  ? <a data-testid={testId} href={href} className={btnCls}>{children}</a>
  : <button data-testid={testId} onClick={onClick} className={btnCls}>{children}</button>);

export const useSong = (id) => {
  const { updateCurrent } = usePlayer();
  const [song, setSongState] = useState(null);
  const [missing, setMissing] = useState(false);
  const setSong = useCallback((d) => { setSongState(d); updateCurrent(d); }, [updateCurrent]);

  useEffect(() => {
    http.get(`/songs/${id}`).then((r) => setSongState(r.data)).catch(() => setMissing(true));
  }, [id]);
  const pending = !!song && (!["completed", "failed"].includes(song.status) || song.timing_status === "syncing");
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => http.get(`/songs/${id}`).then((r) => setSong(r.data)), 3000);
    return () => clearInterval(t);
  }, [pending, id, setSong]);
  return { song, setSong, missing };
};

const PANEL_TEXT = {
  extend: {
    hint: "Sukursime naują, ilgesnę šios dainos versiją: AI pridės naują strofą ir priedainį tuo pačiu stiliumi.",
    placeholder: "pvz.: nauja strofa apie rudenį, liūdnesnė pabaiga", submit: "Kurti ilgesnę versiją",
  },
  cover: {
    hint: "Aprašykite, ką turėtų rodyti viršelis (arba palikite tuščią – AI sugalvos pats).",
    placeholder: "pvz.: naktinis Vilnius, neoninės šviesos lietuje", submit: "Generuoti viršelį",
  },
};

const ActionPanel = ({ song, panel, onDone, setSong }) => {
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const t = PANEL_TEXT[panel];

  const submit = async () => {
    setBusy(true);
    try {
      if (panel === "extend") {
        const { data } = await http.post(`/songs/${song.id}/extend`, { instruction: text });
        toast.success("Kuriama ilgesnė versija…");
        nav(`/daina/${data.id}`);
      } else {
        setSong((await http.post(`/songs/${song.id}/cover`, { prompt: text })).data);
        toast.success("Naujas viršelis paruoštas");
      }
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`song-${panel}-panel`} className="mt-5 p-5 rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] space-y-3 rise">
      <p className="text-sm text-zinc-300">{t.hint}</p>
      <input data-testid={`song-${panel}-input`} value={text} onChange={(e) => setText(e.target.value)} maxLength={500} className={inputCls} placeholder={t.placeholder} />
      <button data-testid={`song-${panel}-submit`} onClick={submit} disabled={busy}
        className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[var(--c-orange)] text-white text-sm font-semibold hover:bg-[#ff7a26] transition-colors disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t.submit}
      </button>
    </div>
  );
};

const PlayButton = ({ song }) => {
  const p = usePlayer();
  const on = p.current?.id === song.id && p.playing;
  return (
    <button data-testid="song-play-button" onClick={() => p.play(song, [song])}
      className="flex items-center gap-2 px-8 py-3 rounded-full bg-[var(--c-orange)] text-white font-semibold glow-orange hover:bg-[#ff7a26] transition-colors duration-200">
      {on ? <Pause className="w-4 h-4" fill="currentColor" /> : <Play className="w-4 h-4" fill="currentColor" />}{on ? "Pauzė" : "Groti"}
    </button>
  );
};

const SyncButton = ({ song, setSong }) => {
  const syncing = song.timing_status === "syncing";
  const sync = async () => {
    try {
      setSong((await http.post(`/songs/${song.id}/sync`)).data);
      toast.success("Sinchronizuojami žodžiai su muzika…");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Btn testId="song-sync-button" onClick={sync}>
      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
      {syncing ? "Sinchronizuojama…" : "AI karaoke sinchronizacija"}
    </Btn>
  );
};

export const SongActions = ({ song, setSong }) => {
  const nav = useNavigate();
  const [panel, setPanel] = useState("");
  const [karaoke, setKaraoke] = useState(false);
  const toggle = (name) => setPanel(panel === name ? "" : name);
  const like = async () => setSong((await http.post(`/songs/${song.id}/like`)).data);
  const sung = song.lyrics && !song.instrumental;

  return (
    <>
      <div className="flex flex-wrap gap-3 mt-8">
        <PlayButton song={song} />
        <Btn testId="song-like-button" onClick={like}><Heart className={`w-4 h-4 ${song.liked ? "text-[var(--c-orange)]" : ""}`} fill={song.liked ? "currentColor" : "none"} />{song.liked ? "Patinka" : "Pamėgti"}</Btn>
        <Btn testId="song-remix-button" onClick={() => nav(`/?remix=${song.id}`)}><Shuffle className="w-4 h-4" />Remiksuoti</Btn>
        <Btn testId="song-cover-version-button" onClick={() => nav(`/?cover=${song.id}`)}><Disc3 className="w-4 h-4" />Cover versija</Btn>
        <AddToPlaylist song={song} label="Į grojaraštį" className={btnCls} />
        <Btn testId="song-share-button" onClick={() => copy(window.location.href, "Nuoroda nukopijuota")}><Share2 className="w-4 h-4" />Dalintis</Btn>
        <Btn testId="song-download-button" href={downloadUrl(song)}><Download className="w-4 h-4" />Atsisiųsti</Btn>
        <Btn testId="song-extend-button" onClick={() => toggle("extend")}><StretchHorizontal className="w-4 h-4" />Prailginti</Btn>
        {song.is_mine && <Btn testId="song-cover-button" onClick={() => toggle("cover")}><ImagePlus className="w-4 h-4" />Naujas viršelis</Btn>}
        {sung && <Btn testId="song-karaoke-button" onClick={() => setKaraoke(true)}><Mic2 className="w-4 h-4" />Karaoke</Btn>}
        {sung && song.is_mine && !song.timing?.length && <SyncButton song={song} setSong={setSong} />}
      </div>
      {panel && <ActionPanel key={panel} song={song} panel={panel} setSong={setSong} onDone={() => setPanel("")} />}
      {karaoke && <KaraokeMode song={song} onClose={() => setKaraoke(false)} />}
    </>
  );
};

const StaticLyrics = ({ lyrics }) => {
  const lines = useMemo(() => lyrics.split("\n").map((l, n) => ({ l, key: `${n}:${l}`, tag: l.trim().startsWith("[") })), [lyrics]);
  return (
    <pre data-testid="song-lyrics" className="whitespace-pre-wrap font-[Figtree] text-base leading-8 text-zinc-200 p-6 rounded-3xl bg-[var(--c-surface)] border border-[var(--c-line)]">
      {lines.map(({ l, key, tag }) => (
        <span key={key} className={tag ? "block font-mono text-xs text-[var(--c-orange)] mt-4" : "block"}>{l || " "}</span>
      ))}
    </pre>
  );
};

export const LyricsSection = ({ song }) => {
  const { current } = usePlayer();
  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">Žodžiai</p>
        <button data-testid="song-copy-lyrics" onClick={() => copy(song.lyrics, "Žodžiai nukopijuoti")} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"><Copy className="w-3.5 h-3.5" />Kopijuoti</button>
      </div>
      {current?.id === song.id ? (
        <div className="h-[460px] p-6 rounded-3xl bg-[var(--c-surface)] border border-[var(--c-line)]">
          <KaraokeLyrics song={song} className="h-full" />
        </div>
      ) : <StaticLyrics lyrics={song.lyrics} />}
    </div>
  );
};
